import { PauseIcon, PlayIcon } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'

interface AudioPreviewProps {
  audioUrl: string
  onCleanup?: () => void
}

export function AudioPreview({ audioUrl, onCleanup }: AudioPreviewProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioBufferRef = useRef<AudioBuffer | null>(null)
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedAtRef = useRef<number>(0)
  const animationFrameRef = useRef<number | null>(null)
  const isLoopingRef = useRef(true)
  const isPlayingRef = useRef(false)

  useEffect(() => {
    const loadAudio = async () => {
      try {
        const response = await fetch(audioUrl)
        const arrayBuffer = await response.arrayBuffer()
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        audioBufferRef.current = audioBuffer
        setDuration(audioBuffer.duration)
      }
      catch (err) {
        console.error('Failed to load audio:', err)
      }
    }

    loadAudio()

    return () => {
      stop()
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
      if (onCleanup) {
        onCleanup()
      }
    }
  }, [audioUrl, onCleanup])

  const stop = () => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop()
      }
      catch {}
      sourceNodeRef.current = null
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setIsPlaying(false)
    isPlayingRef.current = false
    startTimeRef.current = 0
    pausedAtRef.current = 0
  }

  const play = async () => {
    if (!audioContextRef.current || !audioBufferRef.current) return

    const audioContext = audioContextRef.current
    const audioBuffer = audioBufferRef.current

    // Resume context if suspended
    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }

    // Create new source node
    const source = audioContext.createBufferSource()
    source.buffer = audioBuffer
    source.loop = isLoopingRef.current

    // Create gain node for volume control
    const gainNode = audioContext.createGain()
    gainNode.gain.value = 1.0
    source.connect(gainNode)
    gainNode.connect(audioContext.destination)

    gainNodeRef.current = gainNode
    sourceNodeRef.current = source

    // Calculate start offset
    const startOffset = pausedAtRef.current % audioBuffer.duration
    source.start(0, startOffset)

    // Track start time
    const now = audioContext.currentTime
    startTimeRef.current = now - startOffset

    // Handle end of playback (if not looping)
    source.onended = () => {
      if (!isLoopingRef.current) {
        setIsPlaying(false)
        isPlayingRef.current = false
        setProgress(0)
        pausedAtRef.current = 0
      }
    }

    setIsPlaying(true)
    isPlayingRef.current = true

    // Set initial progress
    setProgress(startOffset)

    // Update progress - use a stable function reference
    const updateProgress = () => {
      if (!audioContextRef.current || !audioBufferRef.current) {
        animationFrameRef.current = null
        return
      }

      if (!isPlayingRef.current) {
        animationFrameRef.current = null
        return
      }

      const currentTime = audioContextRef.current.currentTime
      const elapsed = currentTime - startTimeRef.current
      const bufferDuration = audioBufferRef.current.duration

      if (bufferDuration <= 0) {
        animationFrameRef.current = requestAnimationFrame(updateProgress)
        return
      }

      const currentProgress = elapsed % bufferDuration
      const clampedProgress = Math.max(0, Math.min(bufferDuration, currentProgress))

      setProgress(clampedProgress)
      pausedAtRef.current = clampedProgress

      animationFrameRef.current = requestAnimationFrame(updateProgress)
    }

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(updateProgress)
  }

  const pause = () => {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.stop()
      sourceNodeRef.current = null
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setIsPlaying(false)
    isPlayingRef.current = false
  }

  const togglePlayPause = () => {
    if (isPlaying) {
      pause()
    }
    else {
      play()
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioBufferRef.current || !audioContextRef.current) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = x / rect.width
    const newTime = percentage * audioBufferRef.current.duration

    pausedAtRef.current = newTime
    setProgress(newTime)

    // If playing, restart from new position
    if (isPlaying) {
      pause()
      play()
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progressPercentage = duration > 0 ? (progress / duration) * 100 : 0

  return (
    <div className="flex items-center gap-2 flex-1">
      <button
        onClick={togglePlayPause}
        className="px-3 py-2 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-md hover:opacity-90 flex items-center justify-center"
        disabled={!audioBufferRef.current}
      >
        {isPlaying ? <PauseIcon weight="regular" size={16} /> : <PlayIcon weight="regular" size={16} />}
      </button>
      <div className="flex-1 flex items-center gap-2">
        <span className="text-xs text-[#888] min-w-[40px]">{formatTime(progress)}</span>
        <div
          className="flex-1 h-2 bg-neutral-700 rounded-full cursor-pointer relative"
          onClick={handleProgressClick}
        >
          <div
            className="absolute top-0 left-0 h-full bg-blue-500 rounded-full"
            style={{ width: `${progressPercentage}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full -translate-x-1/2"
            style={{ left: `${progressPercentage}%` }}
          />
        </div>
        <span className="text-xs text-[#888] min-w-[40px]">{formatTime(duration)}</span>
      </div>
    </div>
  )
}
